import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Handshake, Percent, FileText, Wallet, Plus, Minus, Loader2, CheckCircle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\u00A0€`;
const COM_LABEL = { a_verser: 'À verser', verse: 'Versée', en_attente_cloture: 'En attente clôture', annulee: 'Annulée' };
const COM_COLOR = { a_verser: '#B57500', verse: '#0B6E4F', en_attente_cloture: 'var(--text-muted)', annulee: '#B03A22' };

export default function PartenairePage() {
  const nav = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [tab, setTab] = useState('demande');
  const [me, setMe] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [err, setErr] = useState(false);

  const H = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [m, d, c] = await Promise.all([
        fetch(`${BACKEND}/api/apporteurs/me`, { headers: H() }).then((r) => r.json()),
        fetch(`${BACKEND}/api/apporteurs/me/dossiers`, { headers: H() }).then((r) => r.json()),
        fetch(`${BACKEND}/api/apporteurs/me/commissions`, { headers: H() }).then((r) => r.json()),
      ]);
      if (m.partenaire) setMe(m); else setErr(true);
      setDossiers(d.dossiers || []);
      setCommissions(c);
    } catch { setErr(true); }
  }, [token, H]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav('/connexion'); return; }
    if (user.role !== 'partenaire' && user.role !== 'admin') { setErr(true); return; }
    load();
  }, [user, authLoading, nav, load]);

  if (authLoading) return null;
  if (err) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div style={{ maxWidth: 560, margin: '80px auto', textAlign: 'center', padding: 20 }}>
        <h2 style={{ color: 'var(--ink)', fontWeight: 800 }}>Espace partenaire</h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 10 }}>Cet espace est réservé aux partenaires validés. Vous n'avez pas encore de profil partenaire actif.</p>
      </div>
      <Footer />
    </div>
  );

  const comp = me?.compteurs || commissions?.compteurs || {};

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div style={{ width: 46, height: 46, borderRadius: 14, background: 'var(--teal-mist)', color: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Handshake size={22} /></div>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--ink)' }}>{me?.partenaire?.raison_sociale || 'Espace partenaire'}</h1>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}><Percent size={13} /> Commission {me?.commission_taux ?? 15}% · à la clôture</div>
          </div>
        </div>

        {/* Compteurs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, margin: '18px 0 24px' }}>
          <Counter label="À verser" value={eur(comp.a_verser)} color="#B57500" testid="pc-a-verser" />
          <Counter label="En attente clôture" value={eur(comp.en_attente_cloture)} color="var(--text-muted)" testid="pc-attente" />
          <Counter label="Déjà versé" value={eur(comp.verse)} color="#0B6E4F" testid="pc-verse" />
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
          {[['demande', 'Nouvelle demande', Handshake], ['dossiers', 'Mes dossiers', FileText], ['commissions', 'Mes commissions', Wallet]].map(([k, l, Ic]) => (
            <button key={k} data-testid={`ptab-${k}`} onClick={() => setTab(k)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: 14, color: tab === k ? 'var(--teal)' : 'var(--text-muted)', borderBottom: tab === k ? '2px solid var(--teal)' : '2px solid transparent' }}>
              <Ic size={15} /> {l}
            </button>
          ))}
        </div>

        {tab === 'demande' && <ComposeDemande token={token} onDone={() => { toast.success('Demande envoyée à l\'exploitant.'); setTab('dossiers'); load(); }} />}

        {tab === 'dossiers' && (
          <div data-testid="partner-dossiers">
            {dossiers.length === 0 ? <Empty msg="Aucun dossier apporté pour l'instant." /> : dossiers.map((d) => (
              <div key={d.quote_id} data-testid={`pdossier-${d.quote_id}`} style={rowCard}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{d.numero} · {d.client_nom}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{d.libelle} · {d.montage === 'pour_lui' ? 'Pour vous' : 'Pour votre client'} · {d.date}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, color: 'var(--ink)' }}>{eur(d.commission_montant)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COM_COLOR[d.commission_statut] }}>{COM_LABEL[d.commission_statut]}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'commissions' && (
          <div data-testid="partner-commissions">
            <div style={{ ...rowCard, background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.3)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Total à venir (à verser)</div>
                <div style={{ fontWeight: 800, fontSize: 22, color: '#B57500' }}>{eur(comp.a_verser)}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
                Versement par virement<br />IBAN {me?.partenaire?.rib?.iban_masque || '—'}
              </div>
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', margin: '20px 0 8px' }}>Historique des versements</h3>
            {(commissions?.versements || []).length === 0 ? <Empty msg="Aucun versement pour l'instant." /> : commissions.versements.map((v) => (
              <div key={v.id} style={rowCard} data-testid={`versement-${v.id}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: 'var(--ink)' }}><CheckCircle size={14} style={{ color: '#0B6E4F', verticalAlign: 'middle' }} /> {v.nb_dossiers} dossier(s)</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{(v.date || '').slice(0, 10)}{v.reference ? ` · réf ${v.reference}` : ''}</div>
                </div>
                <div style={{ fontWeight: 800, color: '#0B6E4F' }}>{eur(v.montant)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function ComposeDemande({ token, onDone }) {
  const [montage, setMontage] = useState('pour_client');
  const [cf, setCf] = useState({ nom: '', email: '', telephone: '' });
  const [ev, setEv] = useState({ type_evenement: 'mariage', date_livraison: '', date_reprise: '', adresse: '' });
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});   // {product_id: qty}
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/products`).then((r) => r.json()).then((d) => setProducts((Array.isArray(d) ? d : d.products) || [])).catch(() => {});
  }, []);

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const sub = (id) => setCart((c) => { const n = (c[id] || 0) - 1; const cc = { ...c }; if (n <= 0) delete cc[id]; else cc[id] = n; return cc; });
  const inCart = Object.entries(cart);

  async function submit() {
    if (!cf.nom || (!cf.telephone && !cf.email)) { toast.error('Coordonnées du client final obligatoires.'); return; }
    if (inCart.length === 0) { toast.error('Ajoutez au moins un produit.'); return; }
    if (!ev.date_livraison) { toast.error('Date de livraison requise.'); return; }
    setBusy(true);
    try {
      const body = {
        montage,
        client_final: cf,
        session: { ...ev, produits: inCart.map(([product_id, quantite]) => ({ product_id, quantite })) },
      };
      const r = await fetch(`${BACKEND}/api/apporteurs/me/demande`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erreur');
      onDone();
    } catch (e) { toast.error(e.message || 'Envoi impossible.'); }
    finally { setBusy(false); }
  }

  const inp = { width: '100%', padding: '11px 13px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--ink)', fontSize: 14 };
  const filtered = products.filter((p) => !q || (p.nom || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="compose-demande">
      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        {[['pour_client', 'Pour mon client', 'Devis au nom du client final, tarif public, il paie'], ['pour_lui', 'Pour moi', 'Le devis est à mon nom, je paie']].map(([k, l, s]) => (
          <button key={k} data-testid={`montage-${k}`} onClick={() => setMontage(k)}
            style={{ flex: 1, textAlign: 'left', padding: 14, borderRadius: 14, cursor: 'pointer', border: montage === k ? '2px solid var(--teal)' : '1px solid var(--border)', background: montage === k ? 'var(--teal-mist)' : 'var(--surface,#fff)' }}>
            <div style={{ fontWeight: 800, color: 'var(--ink)' }}>{l}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s}</div>
          </button>
        ))}
      </div>

      <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginBottom: 10 }}>Client final {montage === 'pour_lui' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>(obligatoire — pour joindre le client)</span>}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <input data-testid="cf-nom" placeholder="Nom du client" value={cf.nom} onChange={(e) => setCf({ ...cf, nom: e.target.value })} style={inp} />
        <input data-testid="cf-tel" placeholder="Téléphone" value={cf.telephone} onChange={(e) => setCf({ ...cf, telephone: e.target.value })} style={inp} />
        <input data-testid="cf-email" placeholder="Email" value={cf.email} onChange={(e) => setCf({ ...cf, email: e.target.value })} style={{ ...inp, gridColumn: '1 / -1' }} />
      </div>

      <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', margin: '20px 0 10px' }}>Événement</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={lab}>Livraison</label><input data-testid="ev-livraison" type="date" value={ev.date_livraison} onChange={(e) => setEv({ ...ev, date_livraison: e.target.value })} style={inp} /></div>
        <div><label style={lab}>Reprise</label><input data-testid="ev-reprise" type="date" value={ev.date_reprise} onChange={(e) => setEv({ ...ev, date_reprise: e.target.value })} style={inp} /></div>
        <input data-testid="ev-adresse" placeholder="Adresse de l'événement" value={ev.adresse} onChange={(e) => setEv({ ...ev, adresse: e.target.value })} style={{ ...inp, gridColumn: '1 / -1' }} />
      </div>

      <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', margin: '20px 0 10px' }}>Matériel</h3>
      <input data-testid="prod-search" placeholder="Rechercher un produit" value={q} onChange={(e) => setQ(e.target.value)} style={{ ...inp, marginBottom: 10 }} />
      <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 12 }}>
        {filtered.slice(0, 40).map((p) => (
          <div key={p.id} data-testid={`prod-${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1 }}><div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: 14 }}>{p.nom}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.categorie} · {p.type}</div></div>
            {cart[p.id] ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => sub(p.id)} style={qtyBtn} data-testid={`minus-${p.id}`}><Minus size={14} /></button>
                <b style={{ minWidth: 18, textAlign: 'center' }}>{cart[p.id]}</b>
                <button onClick={() => add(p.id)} style={qtyBtn} data-testid={`plus-${p.id}`}><Plus size={14} /></button>
              </div>
            ) : (
              <button onClick={() => add(p.id)} style={{ ...qtyBtn, width: 'auto', padding: '6px 12px' }} data-testid={`add-${p.id}`}>Ajouter</button>
            )}
          </div>
        ))}
      </div>

      <button data-testid="submit-demande" onClick={submit} disabled={busy}
        style={{ width: '100%', marginTop: 20, padding: 15, borderRadius: 12, background: 'var(--teal)', color: '#fff', fontWeight: 800, fontSize: 15, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {busy ? <Loader2 size={18} className="animate-spin" /> : `Envoyer la demande (${inCart.length} article${inCart.length > 1 ? 's' : ''})`}
      </button>
    </div>
  );
}

const Counter = ({ label, value, color, testid }) => (
  <div data-testid={testid} style={{ background: 'var(--surface,#fff)', border: '1px solid var(--border)', borderRadius: 14, padding: 16 }}>
    <div style={{ fontWeight: 800, fontSize: 20, color }}>{value}</div>
    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
  </div>
);
const Empty = ({ msg }) => <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>{msg}</div>;
const rowCard = { display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface,#fff)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 10 };
const qtyBtn = { width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--ink)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const lab = { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 };
